/**
 * Workflow Action Handlers
 * Implements the business logic for workflow step actions
 */

import { WorkflowStepId } from '@/types/workflow';
import { useWorkflowStore } from '@/stores/workflowStore';
import api from '@/services/api';
import { router } from '@/router';

/**
 * Helper to get current substeps with defaults
 */
function getCurrentSubsteps(conversationId: string) {
  const store = useWorkflowStore.getState();
  const workflow = store.getWorkflowState(conversationId);
  return workflow?.steps.planning.substeps || {
    storyArchitect: { status: 'pending' as const },
    layoutDesigner: { status: 'pending' as const },
    initiateSlides: { status: 'pending' as const },
  };
}

/**
 * Apply template to conversation working directory
 */
export async function applyTemplate(
  conversationId: string,
  styleId: string,
  templateName?: string
): Promise<void> {
  console.log('🎨 Applying template:', styleId, templateName);

  const store = useWorkflowStore.getState();

  try {
    // Update status to in_progress
    store.updateTemplateSelectionStatus(conversationId, {
      status: 'in_progress',
      progress: 50,
      startedAt: new Date(),
    });

    // Call backend API to copy template files
    const response = await api.post(
      `/conversations/${conversationId}/template/apply`,
      {
        styleId,
        templateName,
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to apply template');
    }

    // Mark template selection as completed
    store.updateTemplateSelectionStatus(conversationId, {
      status: 'completed',
      progress: 100,
      completedAt: new Date(),
      selectedTemplate: styleId,
      selectedTemplateName: templateName || styleId,
      copiedToPath: response.data.copiedToPath,
    });

    // Enable planning step (next step)
    store.updatePlanningStatus(conversationId, {
      canProceed: true,
    });

    // Progress to next step
    store.progressToNextStep(conversationId);

    console.log('✅ Template applied successfully:', {
      styleId,
      copiedToPath: response.data.copiedToPath,
    });
  } catch (error) {
    console.error('❌ Failed to apply template:', error);
    store.updateTemplateSelectionStatus(conversationId, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Failed to apply template',
    });
    throw error;
  }
}

/**
 * Generate video plan - Sequential 3-step deterministic execution
 */
export async function generatePlan(conversationId: string): Promise<void> {
  console.log('📋 Starting deterministic video planning...');

  const store = useWorkflowStore.getState();

  try {
    // Initialize planning state with substeps
    store.updatePlanningStatus(conversationId, {
      status: 'in_progress',
      progress: 0,
      startedAt: new Date(),
      isExpanded: true,
      currentSubstep: 'story_architect',
      substeps: {
        storyArchitect: { status: 'pending' },
        layoutDesigner: { status: 'pending' },
        initiateSlides: { status: 'pending' },
      },
    });

    // STEP 1: Story Architect
    console.log('📖 Step 1/3: Executing story_architect...');
    await executeStoryArchitectStep(conversationId);

    // STEP 2: Layout Designer
    console.log('🎨 Step 2/3: Executing layout_designer...');
    await executeLayoutDesignerStep(conversationId);

    // STEP 3: Initiate Slides
    console.log('📁 Step 3/3: Initializing slides...');
    const slideResult = await executeInitiateSlidesStep(conversationId);

    // Complete planning
    store.updatePlanningStatus(conversationId, {
      status: 'completed',
      progress: 100,
      completedAt: new Date(),
      planId: slideResult?.planPath || 'plan.json',
      sceneCount: slideResult?.totalSlides || 0,
    });

    // Enable slide generation
    store.updateSlideGenerationStatus(conversationId, {
      canProceed: true,
      totalSlides: slideResult?.totalSlides || 0,
    });

    console.log('✅ Video planning complete!', {
      totalSlides: slideResult?.totalSlides || 0,
      planPath: slideResult?.planPath || 'plan.json',
    });
  } catch (error) {
    console.error('❌ Planning failed:', error);
    store.updatePlanningStatus(conversationId, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Planning failed',
    });
    throw error;
  }
}

/**
 * Step 1: Execute story_architect agent
 */
async function executeStoryArchitectStep(conversationId: string): Promise<any> {
  const store = useWorkflowStore.getState();
  const api = (await import('@/services/api')).default;

  // Update substep status
  store.updatePlanningStatus(conversationId, {
    currentSubstep: 'story_architect',
    progress: 0,
    substeps: {
      ...getCurrentSubsteps(conversationId),
      storyArchitect: { status: 'in_progress' },
    },
  });

  // Fetch state.json to get user instructions
  let userInstructions = '';
  try {
    const stateResponse = await api.get(`/project/${conversationId}/state`);
    userInstructions = stateResponse.data.userInstructions || '';
  } catch (error) {
    console.warn('Failed to fetch state.json for userInstructions:', error);
    // Continue with empty string - backend will handle fallback
  }

  // Call API to start story_architect
  const response = await api.post(
    `/conversations/${conversationId}/planning/story-architect`,
    { userInstructions }
  );

  if (!response.data.success) {
    throw new Error(response.data.error);
  }

  const jobId = response.data.jobId;

  // Store job ID
  store.updatePlanningStatus(conversationId, {
    substeps: {
      ...getCurrentSubsteps(conversationId),
      storyArchitect: { status: 'in_progress', jobId },
    },
  });

  // Wait for job to complete (poll via promise)
  const result = await pollAgentJobUntilComplete(jobId, (progress) => {
    // Update progress as job runs (0-33% for story architect)
    store.updatePlanningStatus(conversationId, {
      progress: Math.floor(progress * 33),
    });
  });

  // Mark complete
  store.updatePlanningStatus(conversationId, {
    substeps: {
      ...getCurrentSubsteps(conversationId),
      storyArchitect: { status: 'completed', jobId, result },
    },
  });

  return result;
}

/**
 * Step 2: Execute layout_designer agent
 */
async function executeLayoutDesignerStep(
  conversationId: string
): Promise<any> {
  const store = useWorkflowStore.getState();
  const api = (await import('@/services/api')).default;

  // Update substep status
  store.updatePlanningStatus(conversationId, {
    currentSubstep: 'layout_designer',
    progress: 33,
    substeps: {
      ...getCurrentSubsteps(conversationId),
      layoutDesigner: { status: 'in_progress' },
    },
  });

  // Fetch state.json to get user instructions (for layout preferences if any)
  let userInstructions = '';
  try {
    const stateResponse = await api.get(`/project/${conversationId}/state`);
    userInstructions = stateResponse.data.userInstructions || '';
  } catch (error) {
    console.warn('Failed to fetch state.json for userInstructions:', error);
    // Continue with empty string - backend will handle fallback
  }

  // Call API to start layout_designer
  // Note: No planPath needed - layout_designer will find plan.json in working directory
  const response = await api.post(
    `/conversations/${conversationId}/planning/layout-designer`,
    { userInstructions }
  );

  if (!response.data.success) {
    throw new Error(response.data.error);
  }

  const jobId = response.data.jobId;

  // Store job ID
  store.updatePlanningStatus(conversationId, {
    substeps: {
      ...getCurrentSubsteps(conversationId),
      layoutDesigner: { status: 'in_progress', jobId },
    },
  });

  // Wait for job to complete
  const result = await pollAgentJobUntilComplete(jobId, (progress) => {
    // Update progress (33-66% for layout designer)
    store.updatePlanningStatus(conversationId, {
      progress: 33 + Math.floor(progress * 33),
    });
  });

  // Mark complete
  store.updatePlanningStatus(conversationId, {
    substeps: {
      ...getCurrentSubsteps(conversationId),
      layoutDesigner: { status: 'completed', jobId, result },
    },
  });

  return result;
}

/**
 * Step 3: Initiate slides
 */
async function executeInitiateSlidesStep(
  conversationId: string
): Promise<any> {
  const store = useWorkflowStore.getState();
  const api = (await import('@/services/api')).default;

  // Update substep status
  store.updatePlanningStatus(conversationId, {
    currentSubstep: 'initiate_slides',
    progress: 66,
    substeps: {
      ...getCurrentSubsteps(conversationId),
      initiateSlides: { status: 'in_progress' },
    },
  });

  // Call API (synchronous operation)
  // Note: No parameters needed - endpoint will find plan.json in working directory
  const response = await api.post(
    `/conversations/${conversationId}/planning/initiate-slides`,
    {}
  );

  if (!response.data.success) {
    throw new Error(response.data.error);
  }

  // Mark complete
  store.updatePlanningStatus(conversationId, {
    progress: 100,
    substeps: {
      ...getCurrentSubsteps(conversationId),
      initiateSlides: { status: 'completed', result: response.data.result },
    },
  });

  return response.data.result;
}

/**
 * Helper: Poll agent job until completion
 */
async function pollAgentJobUntilComplete(
  jobId: string,
  onProgress?: (percentage: number) => void
): Promise<any> {
  const api = (await import('@/services/api')).default;

  const maxAttempts = 360; // 360 × 1s = 6 minutes max polling time
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      attempts++;

      // Check if we've exceeded max attempts
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        reject(new Error(`Agent job polling timed out after ${maxAttempts} seconds (${maxAttempts} attempts)`));
        return;
      }

      try {
        const response = await api.get(`/agent-jobs/${jobId}`);
        const job = response.data.job;

        // Update progress callback
        if (onProgress && job.progress?.percentage != null) {
          onProgress(job.progress.percentage / 100);
        }

        // Check terminal status
        if (job.status === 'completed') {
          clearInterval(interval);
          resolve(job.result);
        } else if (job.status === 'failed') {
          clearInterval(interval);
          reject(new Error(job.error || 'Job failed'));
        }
      } catch (error) {
        // Only clear interval on the final attempt or terminal errors
        if (attempts >= maxAttempts || (error as any)?.response?.status === 404) {
          clearInterval(interval);
          reject(error);
        } else {
          // Temporary error, log and continue polling
          console.warn('Polling error (attempt ' + attempts + '/' + maxAttempts + '), retrying...', error);
        }
      }
    }, 1000); // Poll every second
  });
}

/**
 * View template selection
 * Navigates to gallery settings tab to view templates
 */
export async function viewTemplate(conversationId: string): Promise<void> {
  console.log('🎨 Viewing template selection for conversation:', conversationId);

  // Navigate to gallery settings tab
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: undefined, tab: 'settings' },
  });
}

/**
 * View the generated plan
 * Navigates to gallery scenes tab to show the plan
 */
export async function viewPlan(conversationId: string): Promise<void> {
  console.log('👁️ Viewing plan for conversation:', conversationId);

  // Navigate to gallery scenes tab to view plan
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: undefined, tab: 'scenes' },
  });
}

/**
 * Edit the generated plan
 */
export async function editPlan(conversationId: string): Promise<void> {
  console.log('✏️ Pre-populating edit plan template for conversation:', conversationId);

  // Pre-populate message input with edit template
  const messageInput = document.querySelector('[data-message-input="true"]') as HTMLTextAreaElement;

  if (messageInput) {
    const editTemplate = "Edit the plan with the following: []";
    messageInput.value = editTemplate;
    messageInput.focus();

    // Move cursor before the ']' to let user type inside brackets
    const cursorPos = editTemplate.length - 1; // Position before ']'
    messageInput.setSelectionRange(cursorPos, cursorPos);

    // Trigger resize if it's an auto-resize textarea
    const event = new Event('input', { bubbles: true });
    messageInput.dispatchEvent(event);

    console.log('✅ Edit template populated in message input');
  } else {
    console.error('❌ Message input element not found');
  }
}

/**
 * Reset video planning - Clear all planning state and files
 * Allows user to completely restart the planning phase for THIS specific project
 * IMPORTANT: Only affects THIS conversationId, other projects are unaffected
 */
export async function resetPlanning(
  conversationId: string,
  deleteFiles: boolean = true
): Promise<void> {
  const store = useWorkflowStore.getState();
  const api = (await import('@/services/api')).default;

  console.log('🔄 Resetting video planning for conversation:', conversationId);

  // Confirm with user
  const confirmed = window.confirm(
    deleteFiles
      ? 'Are you sure you want to reset video planning? This will delete all planning data (plan.json, scripts, and slide directories). This action cannot be undone.'
      : 'Are you sure you want to reset video planning? Previous files will be backed up with a timestamp.'
  );

  if (!confirmed) {
    console.log('❌ Planning reset cancelled by user');
    return;
  }

  try {
    // 1. Call backend reset endpoint
    const response = await api.post(
      `/conversations/${conversationId}/planning/reset`,
      { deleteFiles }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to reset planning');
    }

    console.log(`✅ Backend reset complete (${response.data.cancelledJobs} jobs cancelled)`);

    // 2. Reset frontend Zustand state for planning
    store.updatePlanningStatus(conversationId, {
      status: 'pending',
      progress: 0,
      canProceed: true,  // Keep enabled if template already selected
      isExpanded: false,
      error: undefined,
      completedAt: undefined,
      startedAt: undefined,
      planId: undefined,
      sceneCount: undefined,
      planUrl: undefined,
      currentSubstep: undefined,
      substeps: {
        storyArchitect: { status: 'pending' },
        layoutDesigner: { status: 'pending' },
        initiateSlides: { status: 'pending' }
      }
    });

    // 3. Reset dependent workflow steps (since they depend on planning)
    store.updateSlideGenerationStatus(conversationId, {
      status: 'pending',
      progress: 0,
      canProceed: false,  // Disable until planning completes
      totalSlides: 0,
      completedSlides: 0,
      slides: [],
      currentSlide: undefined,
      isExpanded: false,
      error: undefined,
      completedAt: undefined,
      startedAt: undefined
    });

    store.updateAudioGenerationStatus(conversationId, {
      status: 'pending',
      progress: 0,
      canProceed: false,  // Disable until planning completes
      audioUrl: undefined,
      duration: undefined,
      voiceId: undefined,
      languageCode: undefined,
      isExpanded: false,
      error: undefined,
      completedAt: undefined,
      startedAt: undefined
    });

    store.updateVideoGenerationStatus(conversationId, {
      status: 'pending',
      progress: 0,
      canProceed: false,  // Disable until audio completes
      manifestPath: undefined,
      totalDuration: undefined,
      slideCount: undefined,
      isExpanded: false,
      error: undefined,
      completedAt: undefined,
      startedAt: undefined,
      currentSubstep: undefined,
      substeps: {
        mapTiming: { status: 'pending' },
        assembly: { status: 'pending' },
        setup: { status: 'pending' }
      }
    });

    console.log('✅ Planning reset complete - ready to create new plan!');
  } catch (error) {
    console.error('❌ Failed to reset planning:', error);
    store.updatePlanningStatus(conversationId, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Failed to reset planning'
    });
    throw error;
  }
}

/**
 * Generate all slides
 */
export async function generateAllSlides(conversationId: string, regenerate = false): Promise<void> {
  console.log('🎨 Generating all slides for conversation:', conversationId, {regenerate});

  const store = useWorkflowStore.getState();

  try {
    const workflow = store.getWorkflowState(conversationId);
    const totalSlides = workflow?.steps.slide_generation.totalSlides || 0;

    // Update status to in_progress
    store.updateSlideGenerationStatus(conversationId, {
      status: 'in_progress',
      progress: 0,
      startedAt: new Date(),
      isExpanded: true,
      totalSlides,
      completedSlides: 0,
    });

    store.progressToNextStep(conversationId);

    // Call backend API to create all slide generation jobs
    const { data: response } = await api.post(`/workflows/${conversationId}/generate-all-slides`, { regenerate });

    if (!response || !response.success) {
      throw new Error(response ? 'Failed to create slide generation jobs' : 'No response from server');
    }

    console.log(`✅ Created ${response.jobsCreated} slide generation jobs:`, response.jobs);

    // Initial progress update based on jobs created
    store.updateSlideGenerationStatus(conversationId, {
      progress: 0,
      totalSlides: response.jobs.length,
      completedSlides: 0,
    });

    // Note: Progress tracking will be handled by polling or websocket updates
    // The jobs are now processing in the worker, and the UI should poll for updates
    // or listen for websocket events to update progress

    console.log('✅ Slide generation jobs enqueued successfully');
  } catch (error) {
    console.error('❌ Slide generation failed:', error);
    store.updateSlideGenerationStatus(conversationId, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Slide generation failed',
    });
    throw error;
  }
}

/**
 * Generate a single slide
 */
export async function generateSlide(
  conversationId: string,
  slideNumber: number
): Promise<void> {
  console.log(`🎨 Generating slide ${slideNumber}`);

  const store = useWorkflowStore.getState();

  try {
    // TODO: Call backend API to generate specific slide
    // const response = await workflowAPI.generateSlide(conversationId, slideNumber);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Update slide status
    const workflow = store.getWorkflowState(conversationId);
    if (workflow) {
      const slideGenStatus = workflow.steps.slide_generation;
      const updatedSlides = [...(slideGenStatus.slides || [])];

      const slideIndex = updatedSlides.findIndex((s) => s.slideNumber === slideNumber);
      if (slideIndex >= 0) {
        updatedSlides[slideIndex] = {
          ...updatedSlides[slideIndex],
          status: 'complete',
          progress: 100,
          completedAt: new Date(),
        };
      } else {
        updatedSlides.push({
          id: `slide-${String(slideNumber).padStart(3, '0')}`,
          slideNumber,
          status: 'complete',
          progress: 100,
          completedAt: new Date(),
        });
      }

      store.updateSlideGenerationStatus(conversationId, {
        slides: updatedSlides,
        completedSlides: updatedSlides.filter((s) => s.status === 'complete').length,
      });
    }

    console.log(`✅ Slide ${slideNumber} generated`);
  } catch (error) {
    console.error(`❌ Slide ${slideNumber} generation failed:`, error);
    throw error;
  }
}

/**
 * View gallery of generated slides
 * Navigates to gallery scenes tab without page reload
 */
export async function viewGallery(conversationId: string): Promise<void> {
  console.log('🖼️ Opening gallery scenes tab for conversation:', conversationId);

  // Client-side navigation to gallery scenes tab
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: undefined, tab: 'scenes' },
  });
}

/**
 * Navigate to gallery and highlight a specific pending slide
 * Uses client-side navigation without page reload
 */
export async function navigateToPendingSlide(conversationId: string, slideNumber: number): Promise<void> {
  console.log(`🎯 Navigating to pending slide ${slideNumber}`);

  // Client-side navigation to gallery with slide parameter
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: slideNumber.toString(), tab: 'scenes' },
  });
}

/**
 * Restart the entire workflow
 */
export async function restartWorkflow(conversationId: string): Promise<void> {
  console.log('🔄 Restarting workflow');

  const store = useWorkflowStore.getState();

  const confirm = window.confirm(
    'Are you sure you want to restart the workflow? This will reset all progress.'
  );

  if (confirm) {
    store.resetWorkflow(conversationId);
    console.log('✅ Workflow restarted');
  }
}

/**
 * Skip to a specific workflow step
 */
export async function skipToStep(
  conversationId: string,
  targetStep: WorkflowStepId
): Promise<void> {
  console.log(`⏭️ Skipping to step: ${targetStep}`);

  const store = useWorkflowStore.getState();
  const workflow = store.getWorkflowState(conversationId);

  if (!workflow) return;

  const steps: WorkflowStepId[] = ['template_selection', 'planning', 'slide_generation'];
  const targetIndex = steps.indexOf(targetStep);
  const currentIndex = steps.indexOf(workflow.currentStep);

  if (targetIndex <= currentIndex) {
    console.warn('Cannot skip to previous or current step');
    return;
  }

  // Mark intermediate steps as completed
  for (let i = currentIndex; i < targetIndex; i++) {
    const step = steps[i];
    store.updateStepStatus(conversationId, step, {
      status: 'completed',
      progress: 100,
      canProceed: true,
    });
  }

  // Enable target step
  store.updateStepStatus(conversationId, targetStep, {
    canProceed: true,
  });

  // Update current step
  const updatedWorkflow = store.getWorkflowState(conversationId);
  if (updatedWorkflow) {
    store.progressToNextStep(conversationId);
  }

  console.log(`✅ Skipped to ${targetStep}`);
}

/**
 * Generate audio narration for the video
 * Calls audio_producer agent to create voiceover from script
 */
export async function generateAudio(
  conversationId: string,
  options?: {
    voiceId?: string;
    language?: string;
    instructions?: string;
    scriptOverride?: string;
  }
): Promise<void> {
  console.log('🎙️ Starting audio generation...');

  const store = useWorkflowStore.getState();

  try {
    // Mark audio generation as in progress
    store.updateAudioGenerationStatus(conversationId, {
      status: 'in_progress',
      progress: 0,
      startedAt: new Date(),
      isExpanded: true,
    });

    // Call backend API to start audio_producer agent
    const response = await api.post(
      `/conversations/${conversationId}/audio/generate`,
      {
        voiceId: options?.voiceId,
        language: options?.language,
        instructions: options?.instructions,
        scriptOverride: options?.scriptOverride,
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to start audio generation');
    }

    const jobId = response.data.jobId;
    console.log(`🎙️ Audio generation job created: ${jobId}`);

    // Store job ID
    store.updateAudioGenerationStatus(conversationId, {
      jobId,
    });

    // Poll job until complete
    const result = await pollAgentJobUntilComplete(jobId, (progress) => {
      store.updateAudioGenerationStatus(conversationId, {
        progress: Math.floor(progress * 100),
      });
    });

    // Mark as completed with result data
    store.updateAudioGenerationStatus(conversationId, {
      status: 'completed',
      progress: 100,
      completedAt: new Date(),
      audioUrl: result.audioUrl,
      duration: result.duration,
      voiceId: result.voiceUsed,
      languageCode: result.languageUsed,
      scriptLength: result.scriptLength,
    });

    console.log('✅ Audio generation complete!', {
      audioUrl: result.audioUrl,
      duration: result.duration,
      voiceUsed: result.voiceUsed,
    });
  } catch (error) {
    console.error('❌ Audio generation failed:', error);
    store.updateAudioGenerationStatus(conversationId, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Audio generation failed',
    });
    throw error;
  }
}

/**
 * Generate video - Sequential 3-step deterministic execution
 * 1. Map timing - Call scene-timing tool to generate timing data
 * 2. Assembly - Call assembly_engineer agent to create manifest.json
 * 3. Setup - Quick state update to mark complete
 */
export async function generateVideo(conversationId: string): Promise<void> {
  console.log('🎬 Starting video generation...');

  const store = useWorkflowStore.getState();

  try {
    // Initialize video generation state with substeps
    store.updateVideoGenerationStatus(conversationId, {
      status: 'in_progress',
      progress: 0,
      startedAt: new Date(),
      isExpanded: true,
      currentSubstep: 'map_timing',
      substeps: {
        mapTiming: { status: 'pending' },
        assembly: { status: 'pending' },
        setup: { status: 'pending' },
      },
    });

    // STEP 1: Map scene timing
    console.log('🕒 Step 1/3: Mapping scene timing...');
    const timingResult = await executeMapTimingStep(conversationId);

    // STEP 2: Assembly engineer
    console.log('🎞️ Step 2/3: Assembling video manifest...');
    const assemblyResult = await executeAssemblyStep(conversationId);

    // STEP 3: Setup / Finalize
    console.log('✅ Step 3/3: Finalizing video project...');
    await executeVideoSetupStep(conversationId);

    // Complete video generation
    store.updateVideoGenerationStatus(conversationId, {
      status: 'completed',
      progress: 100,
      completedAt: new Date(),
      manifestPath: assemblyResult?.manifestPath,
      totalDuration: timingResult?.audioDuration || assemblyResult?.totalDuration,
      slideCount: assemblyResult?.slideCount,
    });

    console.log('✅ Video generation complete!', {
      manifestPath: assemblyResult?.manifestPath,
      totalDuration: assemblyResult?.totalDuration,
      slideCount: assemblyResult?.slideCount,
    });
  } catch (error) {
    console.error('❌ Video generation failed:', error);
    store.updateVideoGenerationStatus(conversationId, {
      status: 'error',
      error: error instanceof Error ? error.message : 'Video generation failed',
    });
    throw error;
  }
}

/**
 * Reset video generation - Clear all video generation state and allow retry
 * Resets both frontend state and backend state.json
 */
export async function resetVideoGeneration(conversationId: string): Promise<void> {
  const store = useWorkflowStore.getState();
  const api = (await import('@/services/api')).default;

  console.log('🔄 Resetting video generation for conversation:', conversationId);

  try {
    // 1. Reset backend state.json (remove video generation fields)
    await api.post(`/conversations/${conversationId}/video-generation/reset`);

    // 2. Reset frontend Zustand state to pending
    store.updateVideoGenerationStatus(conversationId, {
      status: 'pending',
      progress: 0,
      canProceed: true, // Keep enabled since audio is already complete
      isExpanded: false,
      error: undefined,
      completedAt: undefined,
      startedAt: undefined,
      manifestPath: undefined,
      totalDuration: undefined,
      slideCount: undefined,
      currentSubstep: undefined,
      substeps: {
        mapTiming: { status: 'pending' },
        assembly: { status: 'pending' },
        setup: { status: 'pending' },
      },
    });

    console.log('✅ Video generation reset complete - ready to retry!');
  } catch (error) {
    console.error('❌ Failed to reset video generation:', error);
    throw error;
  }
}

/**
 * Step 1: Execute map_scene_timing tool
 * Calls scene-timing API and polls for completion
 */
async function executeMapTimingStep(conversationId: string): Promise<any> {
  const store = useWorkflowStore.getState();
  const api = (await import('@/services/api')).default;

  // Get current substeps helper
  const getCurrentVideoSubsteps = () => {
    const workflow = store.getWorkflowState(conversationId);
    return workflow?.steps.video_generation?.substeps || {
      mapTiming: { status: 'pending' as const },
      assembly: { status: 'pending' as const },
      setup: { status: 'pending' as const },
    };
  };

  // Update substep status
  store.updateVideoGenerationStatus(conversationId, {
    currentSubstep: 'map_timing',
    progress: 0,
    substeps: {
      ...getCurrentVideoSubsteps(),
      mapTiming: { status: 'in_progress' },
    },
  });

  // Call API to start scene timing
  const response = await api.post(
    `/conversations/${conversationId}/video-generation/map-timing`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to start scene timing');
  }

  const taskId = response.data.taskId;
  console.log(`🕒 Scene timing task created: ${taskId}`);

  // Store task ID
  store.updateVideoGenerationStatus(conversationId, {
    substeps: {
      ...getCurrentVideoSubsteps(),
      mapTiming: { status: 'in_progress', taskId },
    },
  });

  // Poll scene timing endpoint until complete (custom polling, not agent job)
  const result = await pollSceneTimingUntilComplete(taskId, conversationId, (progress) => {
    // Update progress (0-33% for map timing)
    store.updateVideoGenerationStatus(conversationId, {
      progress: Math.floor(progress * 33),
    });
  });

  // Mark complete
  store.updateVideoGenerationStatus(conversationId, {
    substeps: {
      ...getCurrentVideoSubsteps(),
      mapTiming: {
        status: 'completed',
        taskId,
        result: {
          audioDuration: result.audio_duration,
          totalScenes: result.total_scenes,
          mappedScenes: result.mapped_scenes,
          unmappedScenes: result.unmapped_scenes,
        },
      },
    },
  });

  return result;
}

/**
 * Step 2: Execute assembly_engineer agent
 */
async function executeAssemblyStep(conversationId: string): Promise<any> {
  const store = useWorkflowStore.getState();
  const api = (await import('@/services/api')).default;

  const getCurrentVideoSubsteps = () => {
    const workflow = store.getWorkflowState(conversationId);
    return workflow?.steps.video_generation?.substeps || {
      mapTiming: { status: 'pending' as const },
      assembly: { status: 'pending' as const },
      setup: { status: 'pending' as const },
    };
  };

  // Update substep status
  store.updateVideoGenerationStatus(conversationId, {
    currentSubstep: 'assembly',
    progress: 33,
    substeps: {
      ...getCurrentVideoSubsteps(),
      assembly: { status: 'in_progress' },
    },
  });

  // Call API to start assembly_engineer
  const response = await api.post(
    `/conversations/${conversationId}/video-generation/assembly`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to start assembly');
  }

  const jobId = response.data.jobId;
  console.log(`🎞️ Assembly engineer job created: ${jobId}`);

  // Store job ID
  store.updateVideoGenerationStatus(conversationId, {
    substeps: {
      ...getCurrentVideoSubsteps(),
      assembly: { status: 'in_progress', jobId },
    },
  });

  // Poll agent job until complete
  const result = await pollAgentJobUntilComplete(jobId, (progress) => {
    // Update progress (33-66% for assembly)
    store.updateVideoGenerationStatus(conversationId, {
      progress: 33 + Math.floor(progress * 33),
    });
  });

  // Mark complete
  store.updateVideoGenerationStatus(conversationId, {
    substeps: {
      ...getCurrentVideoSubsteps(),
      assembly: {
        status: 'completed',
        jobId,
        result: {
          manifestPath: result.manifestPath,
          totalDuration: result.totalDuration,
          slideCount: result.slideCount,
          transitionsUsed: result.transitionsUsed,
        },
      },
    },
  });

  return result;
}

/**
 * Step 3: Finalize setup
 * Quick state.json update to mark video as complete
 */
async function executeVideoSetupStep(conversationId: string): Promise<any> {
  const store = useWorkflowStore.getState();
  const api = (await import('@/services/api')).default;

  const getCurrentVideoSubsteps = () => {
    const workflow = store.getWorkflowState(conversationId);
    return workflow?.steps.video_generation?.substeps || {
      mapTiming: { status: 'pending' as const },
      assembly: { status: 'pending' as const },
      setup: { status: 'pending' as const },
    };
  };

  // Update substep status
  store.updateVideoGenerationStatus(conversationId, {
    currentSubstep: 'setup',
    progress: 66,
    substeps: {
      ...getCurrentVideoSubsteps(),
      setup: { status: 'in_progress' },
    },
  });

  // Call API (synchronous operation)
  const response = await api.post(
    `/conversations/${conversationId}/video-generation/finalize`
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to finalize video');
  }

  // Mark complete
  store.updateVideoGenerationStatus(conversationId, {
    progress: 100,
    substeps: {
      ...getCurrentVideoSubsteps(),
      setup: {
        status: 'completed',
        result: { videoStatus: 'complete' },
      },
    },
  });

  return response.data.result;
}

/**
 * Helper: Poll scene timing task until completion
 * Custom polling for scene-timing tool (not an agent job)
 */
async function pollSceneTimingUntilComplete(
  taskId: string,
  conversationId: string,
  onProgress?: (percentage: number) => void
): Promise<any> {
  const api = (await import('@/services/api')).default;

  const maxAttempts = 180; // 180 × 2s = 6 minutes max polling time
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      attempts++;

      // Check if we've exceeded max attempts
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        reject(new Error(`Scene timing polling timed out after ${maxAttempts * 2} seconds (${maxAttempts} attempts)`));
        return;
      }

      try {
        const response = await api.get(
          `/conversations/${conversationId}/video-generation/map-timing/${taskId}`
        );

        const data = response.data;

        // Update progress callback (estimate progress based on attempts)
        if (onProgress) {
          // Estimate progress: 0-80% based on time elapsed (up to 5 min assumed)
          const estimatedProgress = Math.min(0.8, (attempts * 2) / 300);
          onProgress(estimatedProgress);
        }

        // Check if complete
        if (data.success === true && data.result) {
          clearInterval(interval);
          resolve(data.result);
        } else if (data.success === false) {
          clearInterval(interval);
          reject(new Error(data.error || 'Scene timing failed'));
        }
        // Otherwise still processing, continue polling
      } catch (error) {
        // Don't clear interval on API errors, might be temporary
        console.warn('Polling error (attempt ' + attempts + '/' + maxAttempts + '), retrying...', error);
      }
    }, 2000); // Poll every 2 seconds
  });
}

/**
 * View audio in the gallery
 * Navigates to gallery audio tab
 */
export async function viewAudio(conversationId: string): Promise<void> {
  console.log('🎧 Viewing audio for conversation:', conversationId);

  // Navigate to gallery audio tab
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: undefined, tab: 'audio' },
  });
}

/**
 * Navigate to video preview tab
 */
export async function viewVideo(conversationId: string): Promise<void> {
  console.log('🎥 Viewing video for conversation:', conversationId);

  // Navigate to gallery video tab
  router.navigate({
    to: '/chat/$conversationId/gallery',
    params: { conversationId },
    search: { slide: undefined, tab: 'video' },
  });
}

// Export all handlers
export const workflowActions = {
  applyTemplate,
  viewTemplate,
  generatePlan,
  viewPlan,
  editPlan,
  generateAllSlides,
  generateSlide,
  generateAudio,
  viewAudio,
  generateVideo,
  viewGallery,
  navigateToPendingSlide,
  restartWorkflow,
  skipToStep,
};

// Expose to window for console access during development
if (typeof window !== 'undefined') {
  (window as any).workflowActions = workflowActions;
}
